import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2, AlertCircle, HeartHandshake } from "lucide-react";
import { FadeUp } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Unsubscribe() {
  const [searchParams] = useSearchParams();
  const [state, setState] = useState("loading");
  const [resubscribing, setResubscribing] = useState(false);

  const email = searchParams.get("email");
  const token = searchParams.get("token");

  useEffect(() => {
    if (!email || !token) {
      setState("invalid");
      return;
    }
    axios
      .get(`${API}/unsubscribe`, { params: { email, token } })
      .then(() => setState("done"))
      .catch((err) => setState(err.response?.status === 404 ? "notfound" : "invalid"));
  }, [email, token]);

  const resubscribe = async () => {
    setResubscribing(true);
    try {
      await axios.get(`${API}/resubscribe`, { params: { email, token } });
      setState("resubscribed");
    } catch {
      toast.error("Couldn't resubscribe — please contact support@apiarykeeper.com.");
    } finally {
      setResubscribing(false);
    }
  };

  return (
    <section className="max-w-xl mx-auto px-6 py-28 md:py-40 text-center" data-testid="unsubscribe-page">
      <FadeUp>
        {state === "loading" && (
          <div data-testid="unsubscribe-loading">
            <Loader2 className="w-12 h-12 text-honey-dark mx-auto mb-6 animate-spin" strokeWidth={1.5} />
            <p className="text-ink/60">Updating your preferences...</p>
          </div>
        )}
        {state === "done" && (
          <div data-testid="unsubscribe-success">
            <CheckCircle2 className="w-14 h-14 text-honey-dark mx-auto mb-6" strokeWidth={1.5} />
            <h1 className="font-display text-4xl md:text-5xl font-medium tracking-tighter text-forest mb-4">
              You're unsubscribed.
            </h1>
            <p className="text-ink/60 leading-relaxed mb-10">
              You won't receive marketing emails from ApiaryKeeper anymore. You'll stay
              on the launch list itself, so your early access spot is safe.
            </p>
            <button
              type="button"
              onClick={resubscribe}
              disabled={resubscribing}
              data-testid="resubscribe-button"
              className="inline-flex items-center gap-2 bg-forest text-bone font-semibold px-8 py-3.5 rounded-full text-sm transition-colors duration-300 hover:bg-honey hover:text-forest disabled:opacity-60"
            >
              {resubscribing ? <Loader2 className="w-4 h-4 animate-spin" /> : <HeartHandshake className="w-4 h-4" />}
              Changed your mind? Resubscribe
            </button>
          </div>
        )}
        {state === "resubscribed" && (
          <div data-testid="resubscribe-success">
            <CheckCircle2 className="w-14 h-14 text-honey-dark mx-auto mb-6" strokeWidth={1.5} />
            <h1 className="font-display text-4xl md:text-5xl font-medium tracking-tighter text-forest mb-4">
              Welcome back to the hive. 🐝
            </h1>
            <p className="text-ink/60 leading-relaxed">
              You're resubscribed — launch updates and early access news will find
              their way back to your inbox.
            </p>
          </div>
        )}
        {state === "notfound" && (
          <div data-testid="unsubscribe-notfound">
            <AlertCircle className="w-14 h-14 text-honey-dark mx-auto mb-6" strokeWidth={1.5} />
            <h1 className="font-display text-4xl md:text-5xl font-medium tracking-tighter text-forest mb-4">
              We couldn't find that email.
            </h1>
            <p className="text-ink/60 leading-relaxed">
              That address isn't on our launch list — there's nothing to unsubscribe.
            </p>
          </div>
        )}
        {state === "invalid" && (
          <div data-testid="unsubscribe-invalid">
            <AlertCircle className="w-14 h-14 text-honey-dark mx-auto mb-6" strokeWidth={1.5} />
            <h1 className="font-display text-4xl md:text-5xl font-medium tracking-tighter text-forest mb-4">
              This link doesn't look right.
            </h1>
            <p className="text-ink/60 leading-relaxed">
              The unsubscribe link may be incomplete. Try clicking it directly from your
              email, or contact support@apiarykeeper.com.
            </p>
          </div>
        )}
        <Link
          to="/"
          data-testid="unsubscribe-home-link"
          className="inline-block mt-10 text-sm font-semibold text-forest border-b border-honey pb-1 hover:text-honey-dark transition-colors duration-300"
        >
          Back to apiarykeeper.com
        </Link>
      </FadeUp>
    </section>
  );
}
